import React from 'react';
import clsx from 'clsx';
import numeral from 'numeral';
import { makeStyles } from '@material-ui/styles';
import { MDSTypography } from '@marqvision/mds-v2';
import { IcoMinusFill } from '../../assets';
import MDSButton from '../Button';
import MDSTheme from '../Theme';
import { IMDSFileUploaderProps } from './index';
import { acceptedFilesByType, DefaultMaxFileSize, LocalizedLabel } from './@constants';
import { FileUploaderTypeEnum, IFile, LanguageEnum } from './@types';
import { FileIcon } from './FileIcon';

const useStyles = makeStyles(() => ({
  dropzoneWrapper: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
  },
  dropzoneIcon: {
    '& path': {
      fill: MDSTheme.palette.blue[700],
    },
  },
  dropzoneText: {
    display: 'grid',
    justifyItems: 'center',
    gap: '4px',
  },
  textWrapper: {
    display: 'flex',
    justifyContent: 'center',
    gap: '4px',
  },
  readonlyIcon: {
    width: '24px',
    height: '24px',
    '& path': {
      fill: MDSTheme.palette.bluegray[500],
    },
  },
}));

interface IProps<T> extends IMDSFileUploaderProps<T> {
  onAddButtonClick?: () => void;
}

export const Dropzone = <T extends IFile>(props: IProps<T>): React.ReactNode => {
  const {
    onAddButtonClick,
    renderDropzoneText,
    type = FileUploaderTypeEnum.All,
    isError,
    locale = LanguageEnum.English,
  } = props;
  const classes = useStyles();

  const maxFileSize = Math.min(props.maxFileSize || DefaultMaxFileSize, Number.MAX_SAFE_INTEGER);

  const placeholder =
    props.customAccepts?.placeholder ||
    acceptedFilesByType[type]
      .map((fileType) => fileType.split('/')[1]?.toUpperCase() || fileType.substring(1).toUpperCase())
      .join(', ');

  return (
    <div className={clsx(classes.dropzoneWrapper, 'mds-v1-dropzone')}>
      {onAddButtonClick ? (
        <>
          <FileIcon type={type} />
          <div className={classes.dropzoneText}>
            {renderDropzoneText ? (
              renderDropzoneText(onAddButtonClick)
            ) : (
              <div className={classes.textWrapper}>
                <MDSTypography variant="body" size="m" weight="regular" color="color/content/neutral/secondary/normal">
                  {LocalizedLabel[locale].DragAndDrop}
                </MDSTypography>
                <MDSButton variant="text" size="medium" color="blue" onClick={onAddButtonClick}>
                  {LocalizedLabel[locale].Browse}
                </MDSButton>
              </div>
            )}
            <MDSTypography variant="body" size="xs" weight="regular" color="color/content/neutral/tertiary/normal">
              {placeholder}{' '}
              {type !== FileUploaderTypeEnum.Image &&
                maxFileSize < Number.MAX_SAFE_INTEGER &&
                `(Max. ${numeral(maxFileSize).format('0b', Math.floor)})`}
            </MDSTypography>
          </div>
          {isError && (
            <MDSTypography variant="body" size="s" weight="medium" color="color/content/critical/default/normal">
              {LocalizedLabel[locale].AnErrorOccurred}
            </MDSTypography>
          )}
        </>
      ) : (
        <>
          <IcoMinusFill className={classes.readonlyIcon} />
          <div className={classes.dropzoneText}>
            <MDSTypography variant="body" size="m" weight="regular" color="color/content/neutral/tertiary/normal">
              {LocalizedLabel[locale].Disabled}
            </MDSTypography>
            <MDSTypography variant="body" size="xs" weight="regular" color="color/content/neutral/tertiary/normal">
              {placeholder}{' '}
              {type !== FileUploaderTypeEnum.Image &&
                maxFileSize < Number.MAX_SAFE_INTEGER &&
                `(Max. ${numeral(maxFileSize).format('0b', Math.floor)})`}
            </MDSTypography>
          </div>
        </>
      )}
    </div>
  );
};
